import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Grid,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import {
  BugReport as BugIcon,
  HelpOutline as QuestionIcon,
  AttachFile as AttachFileIcon,
  Send as SendIcon,
  Lightbulb as FeatureIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const SECTIONS = ['Dashboard', 'Materials', 'Inventory', 'Sites', 'Transfers', 'Analytics', 'Users', 'Reports', 'Other'];
const ISSUE_TYPES = ['Bug', 'Question', 'Feature Request'];

const statusColor = (status) => {
  if (status === 'open') return 'error';
  if (status === 'in_progress') return 'warning';
  if (status === 'resolved') return 'success';
  return 'default';
};

const statusLabel = (status) => {
  if (status === 'open') return 'Open';
  if (status === 'in_progress') return 'In Progress';
  if (status === 'resolved') return 'Resolved';
  return status;
};

const RaiseTicketPage = () => {
  const [tab, setTab] = useState(0);
  const [formData, setFormData] = useState({
    section: '',
    issueType: '',
    description: ''
  });
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [myTickets, setMyTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [responseDialog, setResponseDialog] = useState({ open: false, type: '', message: '' });
  const [viewTicket, setViewTicket] = useState(null);

  useEffect(() => {
    fetchMyTickets();
  }, []);

  const fetchMyTickets = async () => {
    try {
      setTicketsLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/tickets/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyTickets(res.data.data.tickets);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    } finally {
      setTicketsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.section || !formData.issueType || !formData.description) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('section', formData.section);
      fd.append('issueType', formData.issueType);
      fd.append('description', formData.description);
      if (screenshotFile) fd.append('screenshot', screenshotFile);

      await axios.post(`${API_BASE_URL}/tickets`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });

      setFormData({ section: '', issueType: '', description: '' });
      setScreenshotFile(null);
      fetchMyTickets();
      setResponseDialog({ open: true, type: 'success', message: 'Ticket raised successfully! You will receive a confirmation email and be notified once resolved.' });
    } catch (err) {
      setResponseDialog({ open: true, type: 'error', message: err.response?.data?.message || 'Failed to raise ticket. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>Support</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Facing an issue? Raise a ticket and we'll resolve it for you.
        </Typography>
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
          <Tab label="Raise a Ticket" />
          <Tab label={`My Tickets (${myTickets.length})`} />
        </Tabs>
      </Paper>

      {tab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>📝 Describe Your Issue</Typography>
          <Divider sx={{ mb: 3 }} />
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth required
                  label="Section *"
                  name="section"
                  value={formData.section}
                  onChange={handleChange}
                  helperText="Which part of the app is affected?"
                >
                  {SECTIONS.map(s => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth required
                  label="Issue Type *"
                  name="issueType"
                  value={formData.issueType}
                  onChange={handleChange}
                >
                  {ISSUE_TYPES.map(t => (
                    <MenuItem key={t} value={t}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {t === 'Bug' && <BugIcon fontSize="small" color="error" />}
                        {t === 'Question' && <QuestionIcon fontSize="small" color="primary" />}
                        {t === 'Feature Request' && <FeatureIcon fontSize="small" color="warning" />}
                        {t}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth required multiline rows={5}
                  label="Description *"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe the issue in detail. Include steps to reproduce if it's a bug (e.g., 'I clicked on Materials → Log Received → submitted form and got an error')."
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<AttachFileIcon />}
                  fullWidth
                >
                  {screenshotFile ? screenshotFile.name : 'Attach Screenshot (Optional)'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => setScreenshotFile(e.target.files[0])}
                  />
                </Button>
                {screenshotFile && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    ✅ {screenshotFile.name} selected
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                  disabled={loading || !formData.section || !formData.issueType || !formData.description}
                >
                  {loading ? 'Submitting...' : 'Submit Ticket'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      )}

      {tab === 1 && (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Ticket #</TableCell>
                  <TableCell>Section</TableCell>
                  <TableCell>Issue Type</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ticketsLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center"><CircularProgress /></TableCell>
                  </TableRow>
                ) : myTickets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No tickets raised yet</TableCell>
                  </TableRow>
                ) : (
                  myTickets.map(ticket => (
                    <TableRow key={ticket.id}>
                      <TableCell><strong>{ticket.ticketNumber}</strong></TableCell>
                      <TableCell>{ticket.section}</TableCell>
                      <TableCell>{ticket.issueType}</TableCell>
                      <TableCell>{ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : new Date(ticket.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Chip
                          label={statusLabel(ticket.status)}
                          color={statusColor(ticket.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Button size="small" onClick={() => setViewTicket(ticket)}>View</Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* View Ticket Dialog */}
      <Dialog open={!!viewTicket} onClose={() => setViewTicket(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          🎫 Ticket: {viewTicket?.ticketNumber}
          <Chip
            label={statusLabel(viewTicket?.status)}
            color={statusColor(viewTicket?.status)}
            size="small"
            sx={{ ml: 2 }}
          />
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Section</Typography>
              <Typography variant="body2" fontWeight={600}>{viewTicket?.section}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Issue Type</Typography>
              <Typography variant="body2" fontWeight={600}>{viewTicket?.issueType}</Typography>
            </Grid>
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">Description</Typography>
              <Paper variant="outlined" sx={{ p: 2, mt: 0.5, bgcolor: 'grey.50' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{viewTicket?.description}</Typography>
              </Paper>
            </Grid>
            {viewTicket?.screenshot && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Screenshot</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Button size="small" variant="outlined" onClick={() => window.open(viewTicket.screenshot, '_blank')}>
                    View Screenshot
                  </Button>
                </Box>
              </Grid>
            )}
            {viewTicket?.adminNotes && (
              <Grid item xs={12} sx={{ mt: 1 }}>
                <Alert severity="success">
                  <Typography variant="caption" fontWeight={600}>Resolution Notes:</Typography>
                  <Typography variant="body2">{viewTicket.adminNotes}</Typography>
                </Alert>
              </Grid>
            )}
            {viewTicket?.resolvedAt && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Resolved on: {new Date(viewTicket.resolvedAt).toLocaleString()}
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewTicket(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Response Dialog */}
      <Dialog open={responseDialog.open} onClose={() => setResponseDialog({ open: false, type: '', message: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>{responseDialog.type === 'success' ? '✅ Success' : '❌ Error'}</DialogTitle>
        <DialogContent>
          <Alert severity={responseDialog.type === 'success' ? 'success' : 'error'} sx={{ mt: 1 }}>
            {responseDialog.message}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setResponseDialog({ open: false, type: '', message: '' });
              if (responseDialog.type === 'success') setTab(1);
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RaiseTicketPage;
